import cn from 'classnames'
import Head from 'next/head'
import {
  AllDocumentTypes,
  PageDocumentData,
  ArticleDocumentData,
  CustomerStoryDocumentData,
  HomePageDocumentData,
  NavigationDocumentData,
} from 'prismicio-types'
import {ReactNode, memo} from 'react'
import {NotificationContextProvider} from 'src/context/NotificationContext'
import AOS from 'aos'
import 'aos/dist/aos.css'
import {HeaderContextProvider} from 'src/context/HeaderContext'
import {useCookie, useEffectOnce} from 'react-use'
import {HrefLangElements} from './HrefElements'
import {ParentUid} from 'pages/[uid]/[subpageUid]'
import {DialogContextProvider} from 'src/context/DialogContext'
import {FooterV2} from './FooterV2'
import {HeaderV2} from './HeaderV2'
import {NotificationBanner} from './NotificationBanner'
import {PageContextProvider} from 'src/context/PageContext'
import dynamic from 'next/dynamic'
import {LinkField} from '@prismicio/client'

const HeaderV3 = dynamic(() => import('./HeaderV3').then((mod) => mod.HeaderV3), {
  ssr: false,
})

export const Layout = memo(function Layout(props: {
  alternateLanguages: Array<AllDocumentTypes['alternate_languages'][number] & {parentUid?: string}>
  breadcrumbs?:
    | PageDocumentData['breadcrumbs']
    | ArticleDocumentData['breadcrumbs']
    | CustomerStoryDocumentData['breadcrumbs']
    | HomePageDocumentData['breadcrumbs']
  children: ReactNode
  className?: string
  navigationData: NavigationDocumentData
  mainClassName?: string
  hideNavigation?: boolean
  isDarkNavigation?: boolean
  pageUid?: string
  locale?: string
  parentUids?: ParentUid[]
  forceFooterV2?: boolean
  funnelType?: string | null
  canonicalUrl?: LinkField
}) {
  const isProduction = typeof window !== 'undefined' && window.location.host.includes('getmoss.com')
  const hasParentPageUid = props.parentUids?.some((el) => el.lang === props.locale)
  const parentUid = hasParentPageUid ? props.parentUids?.find((el) => el.lang === props.locale)?.uid : null
  const isNavV3Enabled = props.navigationData.enable_navbar_v3
  const Header = isNavV3Enabled ? HeaderV3 : HeaderV2
  const filteredLanguages = props.alternateLanguages

  const [internalUserCookie, updateInternalUserCookie] = useCookie('internal_user_id')

  // Initiate AOS
  useEffectOnce(() => {
    if (!internalUserCookie) {
      updateInternalUserCookie('true')
    }

    AOS.init({
      duration: 700,
    })
  })

  return (
    <NotificationContextProvider
      includedUids={props.navigationData?.included_uids}
      notificationId={props.navigationData?.notification_id}
      excludedUids={props.navigationData?.excluded_ids}
    >
      {props.navigationData.notification_text && (
        <NotificationBanner
          message={props.navigationData.notification_text}
          linkField={props.navigationData.notification_link}
          linkText={props.navigationData.notification_link_label}
          dataType={props.navigationData.data_type}
          notificationId={props.navigationData.notification_id}
          pageName={props.pageUid}
          images={props.navigationData.notification_image}
        />
      )}
      <div className={cn('relative grid min-h-screen grid-rows-[auto_1fr_auto] bg-f8f8f8', props.className)}>
        <HrefLangElements
          alternateLanguages={filteredLanguages}
          parentUid={parentUid}
          pageUid={props.pageUid}
          locale={props.locale}
          canonicalUrl={props.canonicalUrl}
        />
        {typeof window !== 'undefined' && (
          <Head>
            {isProduction && (
              <>
                <script
                  dangerouslySetInnerHTML={{
                    __html: `
                    window.dataLayer = window.dataLayer || [];
                    function gtag() {
                        dataLayer.push(arguments);
                    }

                    gtag("consent", "default", {
                        ad_personalization: "denied",
                        ad_storage: "denied",
                        ad_user_data: "denied",
                        analytics_storage: "denied",
                        wait_for_update: 2000 // milliseconds to wait for update
                    });

                    gtag("set", "ads_data_redaction", true);

                    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                    })(window,document,'script','dataLayer','GTM-58T3NG5');
                  `,
                  }}
                />
                <link rel="preconnect" href="https://dev.visualwebsiteoptimizer.com" />
              </>
            )}
          </Head>
        )}
        <DialogContextProvider>
          <PageContextProvider funnelType={props.funnelType}>
            <HeaderContextProvider>
              {!props.hideNavigation && (
                <Header
                  navigationData={props.navigationData}
                  isDarkNavigation={props.isDarkNavigation}
                  key={Date.now()}
                  pageUid={props.pageUid}
                />
              )}
              <main className={`${props.mainClassName} mt-[-60px]`}>{props.children}</main>

              {!props.hideNavigation && (
                <FooterV2
                  alternateLanguages={filteredLanguages}
                  navigationData={props.navigationData}
                  breadcrumbs={props.breadcrumbs}
                  pageUid={props.pageUid}
                />
              )}
            </HeaderContextProvider>
          </PageContextProvider>
        </DialogContextProvider>
        <div id="dialog-container" />
      </div>
    </NotificationContextProvider>
  )
})
